import { ORDER_STATUS_COUNTS_QUERY_KEY } from "@bcc/hooks/use-order-status-counts";
import { useToast } from "@bcc/hooks/use-toast";
import { PURCHASE_ORDERS_QUERY_KEY, usePurchaseOrdersState } from "@client-shared/providers/purchase-orders";
import { createPurchaseOrderShippingDocuments } from "@client-shared/services/api/orders";
import type { PurchaseOrder } from "@shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

interface UseCreateShippingDocumentsResult {
  readonly isCreating: boolean;
  readonly handleCreate: (purchaseOrder: PurchaseOrder) => Promise<void>;
}

export function useCreateShippingDocuments(): UseCreateShippingDocumentsResult {
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [, , utils] = usePurchaseOrdersState();

  const handleCreate = useCallback(
    async (purchaseOrder: PurchaseOrder) => {
      setIsCreating(true);
      try {
        await createPurchaseOrderShippingDocuments({
          purchaseOrderId: purchaseOrder.purchaseOrderId,
          batchId: purchaseOrder.batchId,
          vendorKey: purchaseOrder.vendorKey,
        });
        toast({ title: t("orders.orderDetail.createSuccess") });
      } catch {
        // A failed label print surfaces as the order's "Label Print Error" status
        // after the refetch below, so the raw API error toast is suppressed to
        // avoid showing a technical message to users.
      } finally {
        try {
          // Await the refetch so fresh data lands before the button state resets.
          // The inner finally guarantees the loading state clears even on network errors.
          await utils.fetchPurchaseOrder(purchaseOrder.purchaseOrderId, true);
          await queryClient.invalidateQueries({
            queryKey: [PURCHASE_ORDERS_QUERY_KEY],
          });
          await queryClient.invalidateQueries({
            queryKey: [ORDER_STATUS_COUNTS_QUERY_KEY],
          });
        } finally {
          setIsCreating(false);
        }
      }
    },
    [toast, t, queryClient, utils]
  );

  return { isCreating, handleCreate };
}
